import * as React from 'react';

// The hero's picture: a still of what the app shows, drawn here so the
// landing page needs no screenshot and no board library. A position, the
// move played (red) against the better one (green), and the win chance
// falling at that move. Decorative: screen readers get one label.
const FEN = 'r1bq1rk1/ppp2ppp/2np1n2/2b1p3/2B1P3/2NP1N2/PPP2PPP/R1BQ1RK1';
const GLYPH: Record<string, string> = { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' };
const SQ = 40;

function pieces(fen: string) {
  const out: { x: number; y: number; p: string }[] = [];
  fen.split('/').forEach((row, y) => {
    let x = 0;
    for (const c of row) {
      if (/\d/.test(c)) x += Number(c);
      else out.push({ x: x++, y, p: c });
    }
  });
  return out;
}

const centre = (file: number, rank: number) => [file * SQ + SQ / 2, (8 - rank) * SQ + SQ / 2];

const Arrow: React.FC<{ from: [number, number]; to: [number, number]; cls: string; marker: string }> = ({ from, to, cls, marker }) => {
  const [x1, y1] = centre(...from);
  const [x2, y2] = centre(...to);
  const len = Math.hypot(x2 - x1, y2 - y1);
  const k = (len - 12) / len;
  return <line className={cls} x1={x1} y1={y1} x2={x1 + (x2 - x1) * k} y2={y1 + (y2 - y1) * k} markerEnd={`url(#${marker})`} />;
};

// Win chance across the game, 0–100, one point per move; the drop is the move shown.
const CURVE = [52, 55, 54, 58, 57, 61, 60, 63, 62, 66, 64, 67, 65, 24, 27, 22, 25, 20, 23, 18];
const DROP = 13;

export const Preview: React.FC = () => {
  const w = 8 * SQ;
  const h = 64;
  const px = (i: number) => (i / (CURVE.length - 1)) * w;
  const py = (v: number) => h - (v / 100) * h;
  const path = CURVE.map((v, i) => `${i ? 'L' : 'M'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join(' ');
  return (
    <figure className="landing-preview" aria-label="A game in Continual Chess: the move played, the better move, and the win chance dropping at that move">
      <figcaption className="landing-preview-head">
        <span className="landing-preview-eyebrow">Where the game turned</span>
        <span className="landing-preview-legend">
          <i className="lp-key lp-key-played" /> Played <i className="lp-key lp-key-best" /> Better
        </span>
      </figcaption>
      <svg className="landing-board" viewBox={`0 0 ${w} ${w}`} aria-hidden="true">
        <defs>
          <marker id="lp-head-played" viewBox="0 0 10 10" refX="2" refY="5" markerWidth="3.2" markerHeight="3.2" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" className="lp-head-played" />
          </marker>
          <marker id="lp-head-best" viewBox="0 0 10 10" refX="2" refY="5" markerWidth="3.2" markerHeight="3.2" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" className="lp-head-best" />
          </marker>
        </defs>
        {Array.from({ length: 64 }, (_, i) => (
          <rect key={i} x={(i % 8) * SQ} y={Math.floor(i / 8) * SQ} width={SQ} height={SQ} className={(i % 8) + Math.floor(i / 8) & 1 ? 'lp-dark' : 'lp-light'} />
        ))}
        <rect x={2 * SQ} y={5 * SQ} width={SQ} height={SQ} className="lp-mark" />
        <rect x={3 * SQ} y={3 * SQ} width={SQ} height={SQ} className="lp-mark" />
        {pieces(FEN).map(({ x, y, p }) => (
          <text key={`${x}${y}`} x={x * SQ + SQ / 2} y={y * SQ + SQ * 0.74} className={p === p.toUpperCase() ? 'lp-white' : 'lp-black'}>
            {GLYPH[p.toLowerCase()]}
          </text>
        ))}
        <Arrow from={[2, 3]} to={[3, 5]} cls="lp-arrow-played" marker="lp-head-played" />
        <Arrow from={[7, 2]} to={[7, 3]} cls="lp-arrow-best" marker="lp-head-best" />
      </svg>
      <div className="landing-preview-chart">
        <span className="landing-preview-label">Win chance</span>
        <div className="landing-preview-plot">
          <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
            <line x1="0" x2={w} y1={py(50)} y2={py(50)} className="lp-mid" vectorEffect="non-scaling-stroke" />
            <path d={path} className="lp-curve" vectorEffect="non-scaling-stroke" />
          </svg>
          <i className="lp-drop" style={{ left: `${(DROP / (CURVE.length - 1)) * 100}%`, top: `${100 - CURVE[DROP]}%` }} />
        </div>
      </div>
    </figure>
  );
};
